import { Editor as CoreEditor } from '@tiptap/core';
import { nodeTypeIconMap, anchorTypes } from './const';
import { TableOfContentDataItem } from './types';
import { Node } from '@tiptap/pm/model';
import { animateScroll } from 'react-scroll';
import { articleScrollConfig } from '../../screens/editorial/asset/utility/reactScrollProps';

export const getIconForNodeType = (nodeType: string) => {
  const matchingMapping = nodeTypeIconMap.find((mapping) => mapping.type === nodeType);
  return matchingMapping ? matchingMapping.icon : null;
};

export const getNodeType = (
  nodeType: string,
  dom: HTMLElement,
  attributes: Node['attrs'],
  pos: number,
) => {
  let type = nodeType;

  if (nodeType === 'bloxInline') {
    const blox = dom.querySelectorAll('.blox-inline__wrapper');
    blox.forEach((element) => {
      if (element.id === attributes['id']) {
        return element.getAttribute('data-toc-type') || type;
      }
    });
  }

  if (nodeType === 'horizontalRule') {
    if (
      dom.classList.contains('tncms-page-break') ||
      (attributes &&
        attributes['class'] &&
        attributes['class'].includes('tncms-page-break'))
    ) {
      return 'pageBreak';
    }
  }

  if (nodeType === 'paragraph') {
    if (dom.parentElement?.tagName === 'BLOCKQUOTE') {
      return 'blockquote';
    }

    // Table-related logic
    if (dom.tagName === 'TABLE') {
      return 'table';
    } else if (
      dom.parentElement?.tagName === 'TH' ||
      dom.parentElement?.tagName === 'TD' ||
      dom.tagName === 'TH' ||
      dom.tagName === 'TD' ||
      dom.tagName === 'TR' ||
      dom.tagName === 'THEAD' ||
      dom.tagName === 'TBODY' ||
      dom.tagName === 'TFOOT'
    ) {
      return 'nestedTable';
    }

    if (dom.parentElement?.tagName === 'LI') {
      const listType = dom.parentElement.parentElement?.tagName;
      if (listType === 'UL') {
        return 'bulletList';
      } else if (listType === 'OL') {
        return 'orderedList';
      }
    }
  }

  return type;
};

export const getTrucatedText = (textContent: string) => {
  const content =
    textContent.length > 15 ? `${textContent.slice(0, 15).trim()}\u2026` : textContent;

  return content;
};

// Define functions for processing table of contents data
const getLastHeadingOnLevel = (headings: any, level: any): any => {
  let heading = headings.filter((h: any) => h.level === level).pop();
  if (level !== 0) return heading || getLastHeadingOnLevel(headings, level - 1);
};

const getHeadlineLevel = (node: any, headings: any): any => {
  const level = node.attrs?.level || 1;
  const lastHeading = getLastHeadingOnLevel(headings, level - 1);
  return lastHeading ? lastHeading.level + 1 : 1;
};

const getLinearIndexes = (node: any, headings: any): any => {
  const lastHeading = headings.at(-1);
  return lastHeading ? lastHeading.itemIndex + 1 : 1;
};

export const updateTableOfContents = (editor: CoreEditor) => {
  const headings: { node: any; pos: number }[] = [];
  let processedHeadings: TableOfContentDataItem[] = [];

  // Extract relevant headings from the top-level nodes in the document
  editor.state.doc.descendants((node, pos, parent): any => {
    // Check if the node type is included in anchorTypes
    if (anchorTypes.includes(node.type.name) && parent?.type.name === 'doc') {
      // Push relevant headings to the headings array
      headings.push({ node, pos });
    }
  });

  // Process the extracted headings
  processedHeadings = headings.map((heading, index): any => {
    const dom = editor.view.domAtPos(heading.pos + 1).node;
    const level = heading.node.attrs.level;
    const previousHeading = headings[index - 1];
    const calculatedLevel = getHeadlineLevel(heading, processedHeadings);

    const itemIndex = getLinearIndexes(heading, processedHeadings);

    const processedHeading = {
      itemIndex,
      originalLevel: level,
      level: calculatedLevel,
      textContent: heading.node.textContent,
      pos: heading.pos,
      editor: editor,
      node: heading.node,
      dom: dom,
      attributes: heading.node.attrs,
    };

    processedHeadings = previousHeading
      ? [...processedHeadings, processedHeading]
      : [processedHeading];

    return processedHeading;
  });

  // Update storage with processed data
  return {
    anchors: headings.map((heading): any => editor.view.domAtPos(heading.pos + 1).node),
    content: processedHeadings,
  };
};

export const checkNested = (id: string, dom: HTMLElement, index: number) => {
  const element = document.getElementById(id);
  if (element) {
    const parent = element.parentElement;
    if (parent?.tagName !== 'DIV') {
      return true;
    }
  }
  if (
    dom.parentElement?.tagName === 'TH' ||
    dom.parentElement?.tagName === 'TD' ||
    dom.tagName === 'TH' ||
    dom.tagName === 'TD' ||
    dom.tagName === 'TR' ||
    dom.tagName === 'THEAD' ||
    dom.tagName === 'TFOOT'
  ) {
    return true;
  }
  return false;
};

export const contentScrollTo = (id: string) => {
  const element = document.querySelector(`[data-toc-id="${id}"]`) as HTMLElement;
  animateScroll.scrollTo(element.offsetTop, articleScrollConfig);
};
